import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles, useTheme, alpha, lighten } from "@material-ui/core/styles";
import {
  Typography,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  Button,
  Chip,
  Tooltip,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import RefreshIcon from "@material-ui/icons/Refresh";
import GetAppIcon from "@material-ui/icons/GetApp";
import moment from "moment";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainContainer from "../../components/MainContainer";
import sidebarScrollbar from "../../helpers/sidebarScrollbar";

const formatBRL = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });

const formatBRLShort = (value) =>
  Number(value || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    maximumFractionDigits: 0,
  });

const DATE_FMT = "YYYY-MM-DD";

// períodos prontos → [início, fim]
const PERIODS = [
  { key: "7", label: "7 dias", range: () => [moment().subtract(6, "days"), moment()] },
  { key: "30", label: "30 dias", range: () => [moment().subtract(29, "days"), moment()] },
  { key: "90", label: "90 dias", range: () => [moment().subtract(89, "days"), moment()] },
  { key: "month", label: "Este mês", range: () => [moment().startOf("month"), moment()] },
  { key: "custom", label: "Personalizado" },
];

const initialsOf = (name = "") => {
  const parts = name.replace(/[^A-Za-zÀ-ÿ\s]/g, " ").trim().split(/\s+/);
  return ((parts[0] || "?")[0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
};

const useStyles = makeStyles((theme) => ({
  scroll: {
    // o CssBaseline do app fica fora do ThemeProvider: sem isso o texto
    // herda o preto do tema claro no tema escuro
    color: theme.palette.text.primary,
    flex: 1,
    overflowY: "auto",
    padding: theme.spacing(0, 0.75, 3),
    ...sidebarScrollbar(theme),
  },
  header: {
    color: theme.palette.text.primary,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "space-between",
    gap: theme.spacing(2),
    flexWrap: "wrap",
    padding: theme.spacing(0, 0.75, 2),
  },
  titleBlock: {
    display: "flex",
    alignItems: "flex-start",
    gap: theme.spacing(1),
  },
  title: {
    fontSize: 26,
    fontWeight: 700,
    lineHeight: 1.2,
  },
  muted: {
    color: theme.palette.text.secondary,
    fontSize: 13,
  },
  filters: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  periodChip: {
    fontWeight: 500,
    height: 32,
    borderRadius: 16,
  },
  select: {
    minWidth: 180,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      height: 34,
      fontSize: 13,
      backgroundColor: theme.palette.background.paper,
    },
  },
  dateField: {
    width: 160,
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      height: 34,
      fontSize: 13,
      backgroundColor: theme.palette.background.paper,
    },
  },
  button: {
    textTransform: "none",
    fontWeight: 600,
    borderRadius: 10,
    height: 34,
    color: theme.palette.text.primary,
    borderColor: theme.palette.divider,
    backgroundColor: theme.palette.background.paper,
  },
  kpis: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("md")]: { gridTemplateColumns: "repeat(3, minmax(0, 1fr))" },
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "repeat(2, minmax(0, 1fr))" },
  },
  card: {
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 14,
  },
  kpi: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: theme.spacing(2, 2.25),
  },
  kpiLabel: {
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: theme.palette.text.secondary,
  },
  kpiValue: {
    fontSize: 24,
    fontWeight: 700,
    lineHeight: 1.25,
  },
  kpiDelta: {
    fontSize: 12,
    fontWeight: 600,
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.6fr) minmax(0, 1fr)",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down("sm")]: { gridTemplateColumns: "minmax(0, 1fr)" },
  },
  section: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 2.75),
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 700,
  },
  funnelRow: {
    display: "grid",
    gridTemplateColumns: "140px minmax(0, 1fr) 140px",
    alignItems: "center",
    gap: theme.spacing(1.75),
    [theme.breakpoints.down("xs")]: { gridTemplateColumns: "100px minmax(0, 1fr)" },
  },
  funnelName: {
    fontSize: 14,
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  barTrack: {
    height: 30,
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    overflow: "hidden",
  },
  barFill: {
    height: "100%",
    borderRadius: 8,
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(0, 1.5),
    boxSizing: "border-box",
    fontSize: 13,
    fontWeight: 700,
    color: "#10151F",
    minWidth: 32,
    transition: "width 0.4s ease",
  },
  funnelMeta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    textAlign: "right",
    [theme.breakpoints.down("xs")]: { display: "none" },
  },
  conversionLine: {
    fontSize: 12,
    paddingLeft: `calc(140px + ${theme.spacing(1.75)}px + 4px)`,
    margin: theme.spacing(0.5, 0, 0.75),
    [theme.breakpoints.down("xs")]: { paddingLeft: `calc(100px + ${theme.spacing(1.75)}px)` },
  },
  summaryLine: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(3),
    paddingTop: theme.spacing(1.5),
    borderTop: `1px solid ${theme.palette.divider}`,
    fontSize: 13,
    color: theme.palette.text.secondary,
    "& strong": { color: theme.palette.text.primary },
  },
  smallTrack: {
    height: 8,
    borderRadius: 999,
    backgroundColor: theme.palette.action.hover,
    overflow: "hidden",
  },
  smallFill: {
    height: "100%",
    borderRadius: 999,
  },
  headCell: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    whiteSpace: "nowrap",
  },
  avatar: {
    width: 28,
    height: 28,
    borderRadius: "50%",
    fontSize: 12,
    fontWeight: 700,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: alpha(theme.palette.primary.main, 0.18),
    color: theme.palette.type === "dark" ? "#CFE0FA" : theme.palette.primary.dark,
    flexShrink: 0,
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(6),
  },
  empty: {
    color: theme.palette.text.secondary,
    fontSize: 14,
  },
}));

const CrmReports = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const tone = (c) => (theme.palette.type === "dark" ? lighten(c, 0.3) : c);
  const GREEN = tone("#43A047");
  const RED = tone("#E53935");
  const AMBER = tone("#F59E0B");

  const [pipelines, setPipelines] = useState([]);
  const [users, setUsers] = useState([]);
  const [pipelineId, setPipelineId] = useState("");
  const [userId, setUserId] = useState("");
  const [periodKey, setPeriodKey] = useState("30");
  const [dateFrom, setDateFrom] = useState(moment().subtract(29, "days").format(DATE_FMT));
  const [dateTo, setDateTo] = useState(moment().format(DATE_FMT));
  const [report, setReport] = useState(null);
  const [previous, setPrevious] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [{ data: pipelinesData }, { data: usersData }] = await Promise.all([
          api.get("/pipelines"),
          api.get("/users/"),
        ]);
        setPipelines(pipelinesData);
        setUsers(usersData.users || []);
      } catch (err) {
        toastError(err);
      }
    };
    loadFilters();
  }, []);

  const pickPeriod = (period) => {
    setPeriodKey(period.key);
    if (period.range) {
      const [from, to] = period.range();
      setDateFrom(from.format(DATE_FMT));
      setDateTo(to.format(DATE_FMT));
    }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        pipelineId: pipelineId || undefined,
        userId: userId || undefined,
      };
      // período anterior de mesmo tamanho, para comparar
      const from = moment(dateFrom, DATE_FMT);
      const to = moment(dateTo, DATE_FMT);
      const days = Math.max(1, to.diff(from, "days") + 1);
      const prevTo = from.clone().subtract(1, "day");
      const prevFrom = prevTo.clone().subtract(days - 1, "days");

      const [{ data }, prevResult] = await Promise.all([
        api.get("/opportunities/report", {
          params: { ...params, dateFrom: dateFrom || undefined, dateTo: dateTo || undefined },
        }),
        dateFrom && dateTo
          ? api
              .get("/opportunities/report", {
                params: { ...params, dateFrom: prevFrom.format(DATE_FMT), dateTo: prevTo.format(DATE_FMT) },
              })
              .catch(() => null)
          : Promise.resolve(null),
      ]);
      setReport(data);
      setPrevious(prevResult?.data || null);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [pipelineId, userId, dateFrom, dateTo]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const summary = report?.summary;
  const prevSummary = previous?.summary;

  // funil: etapas em andamento + ganho (perdas ficam no quadro de motivos)
  const funnel = useMemo(() => {
    const list = (report?.stages || [])
      .filter((s) => s.type !== "lost")
      .slice()
      .sort((a, b) => a.order - b.order);
    const max = Math.max(1, ...list.map((s) => s.passedCount));
    return list.map((stage, index) => {
      const next = list[index + 1];
      // entre etapas de venda fechada (ex.: Ganho → Pós-venda) a taxa não faz sentido
      const conversion =
        next && stage.type === "open" && stage.passedCount > 0
          ? Math.round((next.passedCount / stage.passedCount) * 100)
          : null;
      return { ...stage, width: Math.round((stage.passedCount / max) * 100), next, conversion };
    });
  }, [report]);

  const lostTotal = (report?.lostReasons || []).reduce((sum, r) => sum + r.count, 0);

  const periodLabel =
    dateFrom && dateTo
      ? `${moment(dateFrom).format("DD/MM/YYYY")} a ${moment(dateTo).format("DD/MM/YYYY")}`
      : "todo o período";

  const delta = (current, prev, { money = false, points = false, invert = false } = {}) => {
    if (prev == null || current == null) return null;
    const diff = current - prev;
    if (diff === 0) return { text: "igual ao período anterior", color: theme.palette.text.secondary };
    const good = invert ? diff < 0 : diff > 0;
    const arrow = diff > 0 ? "▲" : "▼";
    let text;
    if (points) text = `${arrow} ${Math.abs(diff)} p.p.`;
    else if (money) text = `${arrow} ${formatBRLShort(Math.abs(diff))}`;
    else if (prev > 0) text = `${arrow} ${Math.round((Math.abs(diff) / prev) * 100)}%`;
    else text = `${arrow} ${Math.abs(diff)}`;
    return { text: `${text} vs. período anterior`, color: good ? GREEN : RED };
  };

  const kpis = summary
    ? [
        {
          label: "Negócios criados",
          value: String(summary.created),
          caption: `${formatBRL(summary.createdValue)} em valor`,
          delta: delta(summary.created, prevSummary?.created),
        },
        {
          label: "Ganhos",
          value: formatBRLShort(summary.wonValue),
          color: GREEN,
          caption: `${summary.won} ${summary.won === 1 ? "negócio" : "negócios"}`,
          delta: delta(summary.wonValue, prevSummary?.wonValue, { money: true }),
        },
        {
          label: "Perdidos",
          value: String(summary.lost),
          color: RED,
          caption: formatBRL(summary.lostValue),
          delta: delta(summary.lost, prevSummary?.lost, { invert: true }),
        },
        {
          label: "Conversão",
          value: summary.conversionRate != null ? `${summary.conversionRate}%` : "—",
          caption: "ganhos ÷ (ganhos + perdidos)",
          delta:
            summary.conversionRate != null && prevSummary?.conversionRate != null
              ? delta(Math.round(summary.conversionRate), Math.round(prevSummary.conversionRate), { points: true })
              : null,
        },
        {
          label: "Em aberto",
          value: formatBRLShort(summary.openValue),
          caption: `${summary.open} ${summary.open === 1 ? "negócio" : "negócios"} no funil`,
          delta: null,
        },
      ]
    : [];

  const exportCsv = () => {
    if (!report) return;
    const rows = [];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    rows.push(["Relatório do funil", periodLabel].map(esc).join(";"));
    rows.push("");
    rows.push(["Indicador", "Valor"].map(esc).join(";"));
    rows.push(["Negócios criados", summary.created].map(esc).join(";"));
    rows.push(["Valor criado", summary.createdValue].map(esc).join(";"));
    rows.push(["Ganhos", summary.won].map(esc).join(";"));
    rows.push(["Valor ganho", summary.wonValue].map(esc).join(";"));
    rows.push(["Perdidos", summary.lost].map(esc).join(";"));
    rows.push(["Valor perdido", summary.lostValue].map(esc).join(";"));
    rows.push(["Em aberto", summary.open].map(esc).join(";"));
    rows.push(["Valor em aberto", summary.openValue].map(esc).join(";"));
    rows.push(["Conversão (%)", summary.conversionRate ?? ""].map(esc).join(";"));
    rows.push(["Dias médios até ganhar", summary.avgDaysToClose ?? ""].map(esc).join(";"));
    rows.push("");
    rows.push(["Etapa", "Passaram", "Estão agora", "Dias médios na etapa"].map(esc).join(";"));
    (report.stages || []).forEach((s) =>
      rows.push([s.name, s.passedCount, s.currentCount, s.avgDaysInStage ?? ""].map(esc).join(";"))
    );
    rows.push("");
    rows.push(["Motivo da perda", "Quantidade"].map(esc).join(";"));
    (report.lostReasons || []).forEach((r) => rows.push([r.reason, r.count].map(esc).join(";")));
    rows.push("");
    rows.push(["Responsável", "Abertos", "Ganhos", "Perdidos", "Receita ganha"].map(esc).join(";"));
    (report.byUser || []).forEach((u) =>
      rows.push([u.userName, u.open, u.won, u.lost, u.wonValue].map(esc).join(";"))
    );
    // BOM para o Excel abrir com acentos
    const blob = new Blob(["﻿" + rows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `relatorio-funil-${dateFrom || "inicio"}-a-${dateTo || "hoje"}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  return (
    <MainContainer>
      <div className={classes.header}>
        <div className={classes.titleBlock}>
          <Tooltip title="Voltar ao CRM">
            <IconButton size="small" onClick={() => history.push("/crm")} aria-label="Voltar ao CRM" style={{ marginTop: 2 }}>
              <ArrowBackIcon />
            </IconButton>
          </Tooltip>
          <div>
            <Typography component="h1" className={classes.title}>
              Relatório do funil
            </Typography>
            <Typography className={classes.muted}>
              {(pipelines.find((p) => p.id === pipelineId) || pipelines.find((p) => p.isDefault))?.name ||
                "Funil padrão"}
              {" · negócios criados de "}
              {periodLabel}
            </Typography>
          </div>
        </div>
        <div className={classes.filters}>
          {PERIODS.map((period) => (
            <Chip
              key={period.key}
              clickable
              className={classes.periodChip}
              label={period.label}
              color={periodKey === period.key ? "primary" : "default"}
              variant={periodKey === period.key ? "default" : "outlined"}
              onClick={() => pickPeriod(period)}
              aria-pressed={periodKey === period.key}
            />
          ))}
          {periodKey === "custom" && (
            <>
              <TextField
                type="date"
                size="small"
                variant="outlined"
                className={classes.dateField}
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                inputProps={{ "aria-label": "De" }}
              />
              <TextField
                type="date"
                size="small"
                variant="outlined"
                className={classes.dateField}
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                inputProps={{ "aria-label": "Até" }}
              />
            </>
          )}
          {pipelines.length > 1 && (
            <TextField
              select
              size="small"
              variant="outlined"
              className={classes.select}
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              inputProps={{ "aria-label": "Funil" }}
            >
              <MenuItem value="">Funil padrão</MenuItem>
              {pipelines.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <TextField
            select
            size="small"
            variant="outlined"
            className={classes.select}
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            SelectProps={{ displayEmpty: true }}
            inputProps={{ "aria-label": "Responsável" }}
          >
            <MenuItem value="">Todos os responsáveis</MenuItem>
            {users.map((u) => (
              <MenuItem key={u.id} value={u.id}>
                {u.name}
              </MenuItem>
            ))}
          </TextField>
          <Button
            variant="outlined"
            className={classes.button}
            startIcon={<GetAppIcon />}
            onClick={exportCsv}
            disabled={!report}
          >
            Exportar CSV
          </Button>
          <Tooltip title="Atualizar">
            <span>
              <IconButton size="small" onClick={fetchReport} disabled={loading} aria-label="Atualizar">
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className={classes.scroll}>
        {loading && !report ? (
          <div className={classes.centered}>
            <CircularProgress />
          </div>
        ) : !report ? null : (
          <>
            {/* Indicadores */}
            <div className={classes.kpis}>
              {kpis.map((kpi) => (
                <div key={kpi.label} className={`${classes.card} ${classes.kpi}`}>
                  <span className={classes.kpiLabel}>{kpi.label}</span>
                  <span className={classes.kpiValue} style={kpi.color ? { color: kpi.color } : undefined}>
                    {kpi.value}
                  </span>
                  <span className={classes.muted} style={{ fontSize: 12 }}>
                    {kpi.caption}
                  </span>
                  {kpi.delta && (
                    <span className={classes.kpiDelta} style={{ color: kpi.delta.color }}>
                      {kpi.delta.text}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <div className={classes.grid}>
              {/* Funil */}
              <section className={`${classes.card} ${classes.section}`}>
                <div>
                  <Typography component="h2" className={classes.sectionTitle}>
                    Funil por etapa
                  </Typography>
                  <Typography className={classes.muted}>
                    Quantos negócios do período passaram por cada etapa, quantos seguiram para a
                    próxima e o tempo médio parado nela.
                  </Typography>
                </div>
                {funnel.length === 0 ? (
                  <Typography className={classes.empty}>Nenhuma etapa no funil.</Typography>
                ) : (
                  <div>
                    {funnel.map((stage) => (
                      <div key={stage.id}>
                        <div className={classes.funnelRow}>
                          <span className={classes.funnelName} title={stage.name}>
                            {stage.name}
                          </span>
                          <div className={classes.barTrack}>
                            <div
                              className={classes.barFill}
                              style={{
                                width: `${Math.max(stage.width, 4)}%`,
                                backgroundColor: stage.color || "#90A4AE",
                              }}
                            >
                              {stage.passedCount}
                            </div>
                          </div>
                          <span className={classes.funnelMeta}>
                            {stage.type === "won"
                              ? `${stage.currentCount} agora`
                              : stage.avgDaysInStage != null
                              ? `${Number(stage.avgDaysInStage).toLocaleString("pt-BR")} ${stage.avgDaysInStage === 1 ? "dia" : "dias"} em média`
                              : "—"}
                          </span>
                        </div>
                        {stage.next && stage.conversion !== null && (
                          <div
                            className={classes.conversionLine}
                            style={{ color: stage.conversion < 50 ? AMBER : theme.palette.text.secondary }}
                          >
                            ↓ {stage.conversion}% seguiram para {stage.next.name}
                          </div>
                        )}
                        {(!stage.next || stage.conversion === null) && <div style={{ height: 10 }} />}
                      </div>
                    ))}
                  </div>
                )}
                <div className={classes.summaryLine}>
                  <span>
                    Taxa de conversão:{" "}
                    <strong>{summary.conversionRate != null ? `${summary.conversionRate}%` : "—"}</strong>
                  </span>
                  <span>
                    Tempo médio até ganhar:{" "}
                    <strong>
                      {summary.avgDaysToClose != null
                        ? `${Number(summary.avgDaysToClose).toLocaleString("pt-BR")} dias`
                        : "—"}
                    </strong>
                  </span>
                  <span>
                    Ticket médio ganho:{" "}
                    <strong>{summary.won > 0 ? formatBRL(summary.wonValue / summary.won) : "—"}</strong>
                  </span>
                </div>
              </section>

              {/* Motivos de perda */}
              <section className={`${classes.card} ${classes.section}`}>
                <div>
                  <Typography component="h2" className={classes.sectionTitle}>
                    Motivos de perda
                  </Typography>
                  <Typography className={classes.muted}>
                    {summary.lost} {summary.lost === 1 ? "negócio perdido" : "negócios perdidos"} ·{" "}
                    {formatBRL(summary.lostValue)}
                  </Typography>
                </div>
                {(report.lostReasons || []).length === 0 ? (
                  <Typography className={classes.empty}>Nenhuma perda no período.</Typography>
                ) : (
                  report.lostReasons.map((item) => {
                    const pct = lostTotal ? Math.round((item.count / lostTotal) * 100) : 0;
                    return (
                      <div key={item.reason} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, fontSize: 14 }}>
                          <span>{item.reason}</span>
                          <span className={classes.muted}>
                            <strong style={{ color: theme.palette.text.primary }}>{item.count}</strong> · {pct}%
                          </span>
                        </div>
                        <div className={classes.smallTrack}>
                          <div className={classes.smallFill} style={{ width: `${pct}%`, backgroundColor: RED }} />
                        </div>
                      </div>
                    );
                  })
                )}
              </section>
            </div>

            {/* Por responsável */}
            <section className={`${classes.card} ${classes.section}`} style={{ paddingBottom: 8 }}>
              <div>
                <Typography component="h2" className={classes.sectionTitle}>
                  Por responsável
                </Typography>
                <Typography className={classes.muted}>
                  Negócios criados no período, por quem está à frente de cada um.
                </Typography>
              </div>
              {(report.byUser || []).length === 0 ? (
                <Typography className={classes.empty}>Nenhum negócio no período.</Typography>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <Table size="small" style={{ minWidth: 640 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell className={classes.headCell}>Responsável</TableCell>
                        <TableCell className={classes.headCell} align="right">Abertos</TableCell>
                        <TableCell className={classes.headCell} align="right">Ganhos</TableCell>
                        <TableCell className={classes.headCell} align="right">Perdidos</TableCell>
                        <TableCell className={classes.headCell} style={{ width: 240 }}>Conversão</TableCell>
                        <TableCell className={classes.headCell} align="right">Receita ganha</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {report.byUser.map((row) => {
                        const closed = row.won + row.lost;
                        const conv = closed ? Math.round((row.won / closed) * 100) : null;
                        return (
                          <TableRow key={row.userId ?? "none"}>
                            <TableCell style={{ paddingTop: 10, paddingBottom: 10 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <span className={classes.avatar}>{initialsOf(row.userName)}</span>
                                <span style={{ fontWeight: 600 }}>{row.userName}</span>
                              </div>
                            </TableCell>
                            <TableCell align="right">{row.open}</TableCell>
                            <TableCell align="right" style={{ color: GREEN }}>{row.won}</TableCell>
                            <TableCell align="right" style={{ color: RED }}>{row.lost}</TableCell>
                            <TableCell>
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                <div className={classes.smallTrack} style={{ flex: 1 }}>
                                  <div
                                    className={classes.smallFill}
                                    style={{ width: `${conv || 0}%`, backgroundColor: GREEN }}
                                  />
                                </div>
                                <span style={{ fontSize: 13, width: 40, textAlign: "right" }}>
                                  {conv === null ? "—" : `${conv}%`}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell align="right" style={{ fontWeight: 600 }}>
                              {formatBRL(row.wonValue)}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </section>
          </>
        )}
      </div>
    </MainContainer>
  );
};

export default CrmReports;
